import React, { useState, useEffect, useRef, useMemo } from "react";
import { ArrowLeft, Save, Printer, Loader2, Mail, CheckCircle2, Send, Link2, Plus, Trash2, Search, CreditCard, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { createForm, updateForm, sendFormToCustomer, fetchForm, signLinkFor, fetchParts, createFormPaymentLink } from "@/lib/dashboardApi";
import { money, lineItemAmount, lineItemsSubtotal, computeTotals, changeItemsTotal } from "@/lib/formCompute";
import { expandTemplateFields, normalizeLegacyData, parseCityZip } from "@/lib/formTemplates";
import GuaranteesList from "@/components/GuaranteesList";
import { useCompany } from "@/context/CompanyContext";

const ORANGE = "#F97316";
const inputCls = "w-full bg-white border border-slate-300 rounded-md px-3 py-2 text-slate-900 text-sm focus:outline-none focus:border-[#F97316] focus:ring-2 focus:ring-[#F97316]/15";

const buildPrefill = (customer, template) => {
  if (!customer) return {};
  const name = customer.name || "";
  const phone = customer.phone || "";
  const email = customer.email || "";
  const address = customer.address || "";
  let city = customer.city || "";
  let zip = customer.zip || "";
  if (!city || !zip) {
    const parsed = parseCityZip(address);
    city = city || parsed.city;
    zip = zip || parsed.zip;
  }
  const byKey = {
    owner_name: name, customer_name: name, member_name: name, bill_to_name: name, owner_sign_name: name,
    service_address: address, bill_to_address: address,
    city, zip, bill_to_city: city, bill_to_zip: zip,
    phone, email, bill_to_phone: phone, bill_to_email: email,
  };
  const validKeys = new Set();
  (template.sections || []).forEach((s) => (s.fields || []).forEach((f) => validKeys.add(f.key)));
  const out = {};
  Object.entries(byKey).forEach(([k, v]) => { if (v && validKeys.has(k)) out[k] = v; });
  return out;
};

const Letterhead = ({ company }) => (
  <div className="flex items-start justify-between border-b-2 border-slate-200 pb-4 mb-6">
    <img src="/logo-full.webp" alt="Aquila Air & Heating — Comfort Ready for Liftoff" className="h-16 w-auto" />
    <div className="text-right text-slate-700 text-sm leading-relaxed">
      <p className="font-bold text-slate-900 text-base">{company.name || "Aquila Air & Heating"}</p>
      <p>{company.address || "Cocoa, FL"}</p>
      <p>Phone: {company.phone || "(321) 292-1189"}</p>
      <p>Email: {company.email || "todd@aquilaairandheating.com"}</p>
      <p>Web: {company.domain || "aquilaairandheating.com"}</p>
    </div>
  </div>
);

const lineUid = () => `li_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

const LineItemsEditor = ({ value, onChange }) => {
  const items = Array.isArray(value) ? value : [];
  const update = (i, k, v) => onChange(items.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const add = () => onChange([...items, { description: "", qty: 1, unit_price: "", _uid: lineUid() }]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const [catalog, setCatalog] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  useEffect(() => { fetchParts().then(setCatalog).catch(() => {}); }, []);
  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const matches = q.trim()
    ? catalog.filter((p) => `${p.name} ${p.sku || ""} ${p.notes || ""}`.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 8)
    : [];
  const addFromCatalog = (p) => {
    onChange([...items, { description: p.name, qty: 1, unit_price: p.cost || p.price || "", _uid: lineUid() }]);
    setQ(""); setOpen(false);
    toast.success(`Added ${p.name}`);
  };
  return (
    <div data-testid="lineitems-editor">
      <div className="relative mb-3 no-print" ref={boxRef}>
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
          data-testid="lineitem-part-search" className={`${inputCls} pl-9`} placeholder="Type a part name to add from your Parts catalog…" />
        {open && matches.length > 0 && (
          <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-72 overflow-y-auto" data-testid="lineitem-part-dropdown">
            {matches.map((p) => (
              <button key={p.id} type="button" onClick={() => addFromCatalog(p)} data-testid={`lineitem-part-option-${p.id}`}
                className="w-full flex items-center gap-3 px-3 py-2 hover:bg-slate-50 text-left">
                <span className="min-w-0 flex-1">
                  <span className="block text-sm text-slate-800 truncate">{p.name}</span>
                  <span className="block text-xs text-slate-400 truncate">{p.sku ? `${p.sku}` : ""}</span>
                </span>
                <span className="text-sm font-semibold text-slate-700 shrink-0">{money(p.cost || p.price)}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <div className="grid grid-cols-[1fr_70px_110px_110px_36px] gap-2 px-3 py-2 bg-slate-50 text-[11px] font-semibold text-slate-500 uppercase">
          <span>Description</span><span className="text-right">Qty</span><span className="text-right">Unit Price</span><span className="text-right">Amount</span><span></span>
        </div>
        {items.length === 0 && <p className="text-slate-400 text-sm px-3 py-3">No line items yet — add your first below.</p>}
        {items.map((row, i) => (
          <div key={row._uid || i} className="grid grid-cols-[1fr_70px_110px_110px_36px] gap-2 px-3 py-2 border-t border-slate-100 items-center" data-testid={`lineitem-row-${i}`}>
            <input value={row.description || ""} onChange={(e) => update(i, "description", e.target.value)} className={inputCls} placeholder="e.g. 3-ton condenser + labor" data-testid={`lineitem-desc-${i}`} />
            <input type="number" value={row.qty ?? ""} onChange={(e) => update(i, "qty", e.target.value)} className={`${inputCls} text-right`} data-testid={`lineitem-qty-${i}`} />
            <input type="number" step="0.01" value={row.unit_price ?? ""} onChange={(e) => update(i, "unit_price", e.target.value)} className={`${inputCls} text-right`} data-testid={`lineitem-price-${i}`} />
            <span className="text-right text-sm text-slate-800 font-medium">{money(lineItemAmount(row))}</span>
            <button onClick={() => remove(i)} className="no-print text-slate-400 hover:text-red-600 flex justify-center"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        <div className="flex items-center justify-between px-3 py-2 border-t border-slate-200 bg-slate-50">
          <button onClick={add} className="no-print flex items-center gap-1.5 text-[#F97316] hover:text-[#ea6a0c] text-sm font-semibold" data-testid="lineitem-add">
            <Plus className="w-4 h-4" /> Add line
          </button>
          <span className="text-sm text-slate-600">Subtotal: <strong className="text-slate-900">{money(lineItemsSubtotal(items))}</strong></span>
        </div>
      </div>
    </div>
  );
};

const changeUid = () => `ci_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
const ChangeItemsEditor = ({ value, onChange }) => {
  const items = Array.isArray(value) ? value : [];
  const update = (i, k, v) => onChange(items.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const add = () => onChange([...items, { description: "", amount: "", notes: "", _uid: changeUid() }]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <div data-testid="change-items-editor">
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <div className="grid grid-cols-[1fr_130px_36px] gap-2 px-3 py-2 bg-slate-50 text-[11px] font-semibold text-slate-500 uppercase">
          <span>Description</span><span className="text-right">Amount</span><span></span>
        </div>
        {items.length === 0 && <p className="text-slate-400 text-sm px-3 py-3">No change items yet — add the additional work below.</p>}
        {items.map((row, i) => (
          <div key={row._uid || i} className="px-3 py-2 border-t border-slate-100 space-y-2" data-testid={`change-item-row-${i}`}>
            <div className="grid grid-cols-[1fr_130px_36px] gap-2 items-center">
              <input value={row.description || ""} onChange={(e) => update(i, "description", e.target.value)} className={inputCls} placeholder="e.g. Revised change order" data-testid={`change-item-desc-${i}`} />
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                <input type="number" step="0.01" value={row.amount ?? ""} onChange={(e) => update(i, "amount", e.target.value)} className={`${inputCls} pl-7 text-right`} placeholder="0.00" data-testid={`change-item-amount-${i}`} />
              </div>
              <button onClick={() => remove(i)} className="no-print text-slate-400 hover:text-red-600 flex justify-center"><Trash2 className="w-4 h-4" /></button>
            </div>
            <input value={row.notes || ""} onChange={(e) => update(i, "notes", e.target.value)} className={`${inputCls} text-xs`} placeholder="Optional notes" />
          </div>
        ))}
        <div className="flex items-center justify-between px-3 py-2 border-t border-slate-200 bg-slate-50">
          <button onClick={add} className="no-print flex items-center gap-1.5 text-[#F97316] hover:text-[#ea6a0c] text-sm font-semibold" data-testid="change-item-add">
            <Plus className="w-4 h-4" /> Add change item
          </button>
          <span className="text-sm text-slate-600">Total Additional Work: <strong className="text-slate-900" data-testid="change-items-total">{money(changeItemsTotal(items))}</strong></span>
        </div>
      </div>
    </div>
  );
};

const ChangeBalanceSummary = ({ data }) => {
  const c = computeTotals("change_total", data);
  const signed = (n) => `${n < 0 ? "-" : "+"}${money(Math.abs(n))}`;
  return (
    <div className="grid sm:grid-cols-2 gap-4" data-testid="computed-change_total">
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm">
        <p className="text-[11px] font-semibold text-slate-500 uppercase mb-2">Amount Remaining</p>
        <div className="flex justify-between py-1 text-slate-600"><span>Original Contract Price</span><span>{money(c.original)}</span></div>
        <div className="flex justify-between py-1 text-slate-600"><span>Previously Paid</span><span>-{money(c.previouslyPaid)}</span></div>
        <div className="flex justify-between py-1 border-t border-slate-200 mt-1 pt-1.5 text-slate-700"><span>Original Balance Remaining</span><span>{money(c.origBalanceRemaining)}</span></div>
        <div className="flex justify-between py-1 text-slate-600"><span>Net Change / Additional Work</span><span>{signed(c.netChange)}</span></div>
        <div className="flex justify-between items-center border-t-2 border-[#F97316]/40 mt-2 pt-2">
          <span className="font-bold text-slate-900">CURRENT BALANCE REMAINING</span>
          <span className="text-xl font-extrabold text-[#F97316]" data-testid="change-current-balance">{money(c.currentBalanceRemaining)}</span>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg p-4 text-sm self-start">
        <p className="text-[11px] font-semibold text-slate-500 uppercase mb-2">Contract Value</p>
        <div className="flex justify-between py-1 text-slate-600"><span>Revised Contract Value</span><span className="font-semibold text-slate-900" data-testid="change-revised-value">{money(c.revisedContractValue)}</span></div>
        <div className="flex justify-between py-1 text-slate-600"><span>Previously Paid</span><span>{money(c.previouslyPaid)}</span></div>
        <div className="flex justify-between py-1 text-slate-600"><span>Current Balance Remaining</span><span>{money(c.currentBalanceRemaining)}</span></div>
      </div>
    </div>
  );
};

const ComputedTotals = ({ type, data }) => {
  if (type === "change_total") return <ChangeBalanceSummary data={data} />;
  const c = computeTotals(type, data);
  const Row = ({ label, val, bold }) => (
    <div className={`flex justify-between py-1.5 ${bold ? "border-t border-slate-300 mt-1 pt-2 font-bold text-slate-900" : "text-slate-600"}`}>
      <span>{label}</span><span>{money(val)}</span>
    </div>
  );
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 max-w-sm ml-auto text-sm" data-testid={`computed-${type}`}>
      <Row label="Subtotal (from line items)" val={c.subtotal} />
      <Row label={`Tax (${c.rate || 0}%)`} val={c.tax} />
      <Row label="Total" val={c.total} bold />
    </div>
  );
};

const FieldInput = ({ field, value, onChange }) => {
  if (field.type === "lineitems") return <LineItemsEditor value={value} onChange={onChange} />;
  if (field.type === "change_items") return <ChangeItemsEditor value={value} onChange={onChange} />;
  const v = value ?? (field.default !== undefined ? field.default : "");
  if (field.type === "checkbox") {
    return (
      <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer py-1">
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 mt-0.5 accent-[#F97316]" data-testid={`field-${field.key}`} />
        {field.label}
      </label>
    );
  }
  if (field.type === "dropdown") {
    return (
      <select value={v} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={`field-${field.key}`}>
        <option value="">— Select —</option>
        {(field.options || []).map((o) => (<option key={o} value={o}>{o}</option>))}
      </select>
    );
  }
  if (field.type === "textarea") {
    return <textarea rows={2} value={v} onChange={(e) => onChange(e.target.value)} className={`${inputCls} resize-none`} data-testid={`field-${field.key}`} />;
  }
  const inputType = field.type === "date" ? "date" : (field.type === "number" || field.type === "money") ? "number" : "text";
  return (
    <div className="relative">
      {field.type === "money" && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>}
      <input type={inputType} value={v} onChange={(e) => onChange(e.target.value)} className={`${inputCls} ${field.type === "money" ? "pl-7" : ""}`} data-testid={`field-${field.key}`} />
    </div>
  );
};

const FormEditor = ({ template: rawTemplate, existing, customer, onBack, onSaved }) => {
  const { company } = useCompany();
  const template = useMemo(() => expandTemplateFields(rawTemplate), [rawTemplate]);
  const [data, setData] = useState(() => (existing?.data ? normalizeLegacyData(existing.data) : buildPrefill(customer, template)));
  const [record, setRecord] = useState(existing || null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [payLoading, setPayLoading] = useState(false);
  const setField = (key, val) => setData((p) => ({ ...p, [key]: val }));
  const supportsPayment = ["invoice", "change_order", "install", "estimate"].includes(template.id);

  const ensureSaved = async () => {
    if (record?.id) { await updateForm(record.id, { data, title: template.short }); return record; }
    const created = await createForm({
      customer_id: customer?.id || "",
      customer_name: customer?.name || data.owner_name || data.customer_name || data.bill_to_name || "Unassigned",
      form_type: template.id,
      title: template.short,
      data,
    });
    setRecord(created);
    return created;
  };

  const handleSave = async () => {
    setSaving(true);
    try { await ensureSaved(); toast.success("Document saved to customer profile"); onSaved && onSaved(); }
    catch { toast.error("Failed to save document"); }
    finally { setSaving(false); }
  };

  const handleSend = async () => {
    const prefill = data.email || data.bill_to_email || "";
    const email = window.prompt("Send this document for signature to which email?", prefill);
    if (!email) return;
    setSending(true);
    const toastId = toast.loading(`Saving & sending to ${email}…`);
    try {
      const rec = await ensureSaved();
      await sendFormToCustomer(rec.id, email, window.location.origin);
      const refreshed = await fetchForm(rec.id);
      setRecord(refreshed);
      toast.success(`Email sent to ${email}`, { id: toastId });
      onSaved && onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send email.", { id: toastId });
    } finally { setSending(false); }
  };

  const copyLink = async () => {
    const rec = record?.id ? record : await ensureSaved();
    const url = signLinkFor(rec);
    try { await navigator.clipboard.writeText(url); toast.success("Sign link copied to clipboard"); }
    catch { window.prompt("Copy this signing link:", url); }
  };

  const handlePaymentLink = async () => {
    setPayLoading(true);
    const toastId = toast.loading("Creating Square payment link…");
    try {
      const rec = await ensureSaved();
      const res = await createFormPaymentLink(rec.id);
      setField("payment_url", res.payment_url);
      setField("payment_amount", res.amount);
      toast.success(res.training ? "Payment link created (sandbox/test)" : `Payment link ready · ${money(res.amount)}`, { id: toastId });
      try { await navigator.clipboard.writeText(res.payment_url); } catch (_) {}
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create payment link.", { id: toastId, duration: 7000 });
    } finally { setPayLoading(false); }
  };

  return (
    <div>
      <div className="no-print flex flex-wrap items-center gap-3 mb-5">
        <button onClick={onBack} className="flex items-center gap-2 text-slate-600 hover:text-[#F97316] font-medium text-sm" data-testid="form-back-btn">
          <ArrowLeft className="w-4 h-4" /> Back to Forms
        </button>
        {record?.signed && (
          <span className="flex items-center gap-1.5 bg-[#F97316]/10 text-[#F97316] px-3 py-1 rounded-full text-sm font-semibold">
            <CheckCircle2 className="w-4 h-4" /> Signed{record.signed_at ? ` · ${new Date(record.signed_at).toLocaleDateString()}` : ""}
          </span>
        )}
        {record?.status === "sent" && !record?.signed && (
          <span className="flex items-center gap-1.5 bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-sm font-semibold">
            <Send className="w-3.5 h-3.5" /> Sent · awaiting signature
          </span>
        )}
        <div className="ml-auto flex flex-wrap gap-2">
          <button onClick={() => window.print()} className="flex items-center gap-2 bg-white border border-slate-300 hover:border-[#F97316] hover:text-[#F97316] text-slate-600 px-4 py-2 rounded-lg text-sm font-medium" data-testid="form-print-btn">
            <Printer className="w-4 h-4" /> Print / PDF
          </button>
          <button onClick={handleSend} disabled={sending} className="flex items-center gap-2 bg-white border border-slate-300 hover:border-[#F97316] hover:text-[#F97316] text-slate-600 px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50" data-testid="form-email-btn">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Email to Customer
          </button>
          <button onClick={copyLink} data-testid="form-copy-link-btn" className="flex items-center gap-2 bg-white border border-slate-300 hover:border-[#F97316] hover:text-[#F97316] text-slate-600 px-4 py-2 rounded-lg text-sm font-medium">
            <Link2 className="w-4 h-4" /> Copy Sign Link
          </button>
          {supportsPayment && (
            <button onClick={handlePaymentLink} disabled={payLoading} data-testid="form-payment-link-btn" className="flex items-center gap-2 bg-white border border-slate-300 hover:border-[#F97316] hover:text-[#F97316] text-slate-600 px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50">
              {payLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />} Payment Link (Square)
            </button>
          )}
          <button onClick={handleSave} disabled={saving} data-testid="form-save-btn" className="flex items-center gap-2 bg-[#F97316] hover:bg-[#ea6a0c] text-white px-5 py-2 rounded-lg text-sm font-semibold disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      <div className="print-area bg-white border border-slate-200 rounded-xl p-8 shadow-sm max-w-4xl mx-auto" style={{ fontFamily: "Arial, Helvetica, sans-serif" }} data-testid="form-document">
        <Letterhead company={company || {}} />
        <h1 className="text-center text-2xl font-bold text-slate-900 mb-1 uppercase">{template.name}</h1>
        {(() => {
          const shownName = customer?.name || data.owner_name || data.customer_name || data.bill_to_name || "";
          const shownAddr = customer?.address || data.service_address || data.bill_to_address || "";
          if (!shownName && !shownAddr) return null;
          return (
            <p className="text-center text-slate-500 text-sm mb-6" data-testid="form-customer-header">
              {shownName && <>Prepared for <span className="font-semibold text-slate-700">{shownName}</span></>}
              {shownName && shownAddr && " · "}
              {shownAddr}
            </p>
          );
        })()}
        {template.sections.map((section, si) => (
          <div key={si} className="mb-6">
            {section.title && <h2 className="text-lg font-bold text-slate-900 border-b border-slate-200 pb-1 mb-3">{section.title}</h2>}
            {section.intro && <p className="text-slate-600 text-sm leading-relaxed mb-3 whitespace-pre-line">{section.intro}</p>}
            {section.computed && <ComputedTotals type={section.computed} data={data} />}
            {section.guarantees && <GuaranteesList variant="print" />}
            {section.fields.length > 0 && (
              <div className={section.fields[0].type === "checkbox" ? "grid sm:grid-cols-1 gap-x-6" : "grid sm:grid-cols-2 gap-4"}>
                {section.fields.map((field) => (
                  <div key={field.key} className={["textarea", "checkbox", "lineitems", "change_items"].includes(field.type) ? "sm:col-span-2" : ""}>
                    {field.type !== "checkbox" && <label className="block text-xs font-semibold text-slate-500 mb-1">{field.label}</label>}
                    <FieldInput field={field} value={data[field.key]} onChange={(v) => setField(field.key, v)} />
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
        {data.owner_signature_img && (
          <div className="mt-6">
            <p className="text-xs font-semibold text-slate-500 mb-1">Customer Electronic Signature</p>
            <img src={data.owner_signature_img} alt="Signature" className="h-20 border-b border-slate-300" />
            {data.owner_sign_name && <p className="text-sm text-slate-700 mt-1">{data.owner_sign_name} · {data.owner_sign_date || ""}</p>}
          </div>
        )}
        {data.payment_url && (
          <div className="mt-6 border border-[#F97316]/30 bg-[#F97316]/5 rounded-lg p-4" data-testid="form-payment-block">
            <p className="text-sm font-bold text-slate-900 flex items-center gap-2"><CreditCard className="w-4 h-4 text-[#F97316]" /> Pay Online Securely (Square)</p>
            {data.payment_amount != null && data.payment_amount !== "" && (
              <p className="text-sm text-slate-700 mt-1">Amount due: <strong>{money(data.payment_amount)}</strong></p>
            )}
            <a href={data.payment_url} target="_blank" rel="noopener noreferrer" data-testid="form-payment-url"
              className="inline-flex items-center gap-1.5 mt-2 text-[#F97316] font-semibold hover:underline break-all">
              <ExternalLink className="w-4 h-4 shrink-0" /> {data.payment_url}
            </a>
          </div>
        )}
        <div className="border-t border-slate-200 mt-8 pt-4 text-center text-[11px] text-slate-500 leading-relaxed">
          <p>{company?.license || "FL State Certified & Insured"}. {company?.name || "Aquila Air & Heating"} · {company?.phone || "(321) 292-1189"} · {company?.email || "todd@aquilaairandheating.com"}</p>
        </div>
      </div>
    </div>
  );
};

export default FormEditor;
